import { useState, useEffect, useRef } from "react";
import { getApiUrl } from '../../config/api';

let jobsCache = null;
let lastJobsFetchTime = 0;
const CACHE_DURATION_MS = 5 * 60 * 1000;
const CACHE_DURATION = CACHE_DURATION_MS;

/** True if jobs cache is valid (for prefetch to skip duplicate fetch). */
export function isJobsCacheValid() {
  return jobsCache && Date.now() - lastJobsFetchTime < CACHE_DURATION_MS;
}

/** Called by overviewPrefetch / myJobsPrefetch to warm cache. */
export function setJobsCacheFromPrefetch(mappedJobs) {
  if (!Array.isArray(mappedJobs)) return;
  jobsCache = mappedJobs;
  lastJobsFetchTime = Date.now();
}

const mapJobs = (data) =>
  (data || []).map((job) => ({
    id: job.job_id,
    jobTitle: job.job_title,
    jobType: job.job_type,
    experienceRange: job.experience_range,
    location: job.location,
    status: job.status,
    expirationDate: job.application_deadline || job.expiration_date,
    applications: job.applicants,
    ...job,
  }));

const sortJobsPinnedFirst = (jobs) => {
  return [...(jobs || [])].sort((a, b) => {
    const ap = a?.is_pinned ? 1 : 0;
    const bp = b?.is_pinned ? 1 : 0;
    if (bp !== ap) return bp - ap;

    const at = a?.created_at ? Date.parse(a.created_at) : 0;
    const bt = b?.created_at ? Date.parse(b.created_at) : 0;
    return bt - at;
  });
};

const useJob = () => {
  const [jobs, setJobs] = useState(() => {
    const now = Date.now();
    if (jobsCache && now - lastJobsFetchTime < CACHE_DURATION) return jobsCache;
    return [];
  });
  const [loading, setLoading] = useState(() => {
    const now = Date.now();
    return !jobsCache || now - lastJobsFetchTime >= CACHE_DURATION;
  });
  const isInitialMount = useRef(true);

  const updateJobsEverywhere = (updater) => {
    setJobs((prev) => {
      const next = sortJobsPinnedFirst(updater(prev));
      jobsCache = next;
      lastJobsFetchTime = Date.now();
      return next;
    });
  };

  useEffect(() => {
    const fetchJobs = async () => {
      const employerEmail = localStorage.getItem('employer_email');
      if (!employerEmail) {
        setLoading(false);
        return;
      }

      const now = Date.now();
      const hasFreshCache = jobsCache && now - lastJobsFetchTime < CACHE_DURATION;
      if (hasFreshCache && isInitialMount.current) {
        setJobs(jobsCache);
        setLoading(false);
      }

      // Keep cached jobs visible for a fast first paint, but always
      // revalidate on entry. A role can be closed from the Hiring Workflow
      // while this five-minute cache is still valid; returning early here
      // made the All Jobs badge incorrectly remain Open until cache expiry.
      if (!hasFreshCache) setLoading(true);
      try {
        const response = await fetch(
          getApiUrl(`/api/get_jd?company_email=${encodeURIComponent(employerEmail)}`),
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error("Failed to fetch JDs");
        }

        const data = await response.json();
        const mappedJobs = sortJobsPinnedFirst(mapJobs(data));
        jobsCache = mappedJobs;
        lastJobsFetchTime = Date.now();
        setJobs(mappedJobs);
      } catch (error) {
        console.error("Error fetching jobs:", error.message);
        if (jobsCache) setJobs(jobsCache);
        else setJobs([]);
      } finally {
        setLoading(false);
        isInitialMount.current = false;
      }
    };

    fetchJobs();
  }, []);

  const togglePinJob = async (jobId) => {
    if (!jobId) throw new Error("Missing jobId");

    const response = await fetch(
      getApiUrl(`/api/jd/${encodeURIComponent(jobId)}/pin`),
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to toggle pin");
    }

    const data = await response.json();
    const isPinned = Boolean(data?.is_pinned);

    updateJobsEverywhere((prev) =>
      (prev || []).map((j) => {
        const id = j?.id ?? j?.job_id;
        if (String(id) !== String(jobId)) return j;
        return { ...j, is_pinned: isPinned };
      })
    );

    return isPinned;
  };

  const deleteJob = async (jobId) => {
    if (!jobId) throw new Error("Missing jobId");

    const response = await fetch(
      getApiUrl(`/api/jd/${encodeURIComponent(jobId)}`),
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to delete job");
    }

    updateJobsEverywhere((prev) =>
      (prev || []).filter((j) => String(j?.id ?? j?.job_id) !== String(jobId))
    );

    return true;
  };

  const refreshJobs = async () => {
    jobsCache = null;
    lastJobsFetchTime = 0;
    setLoading(true);

    const employerEmail = localStorage.getItem('employer_email');
    if (!employerEmail) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        getApiUrl(`/api/get_jd?company_email=${encodeURIComponent(employerEmail)}`),
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      if (!response.ok) throw new Error("Failed to fetch JDs");

      const data = await response.json();
      const mappedJobs = sortJobsPinnedFirst(mapJobs(data));
      jobsCache = mappedJobs;
      lastJobsFetchTime = Date.now();
      setJobs(mappedJobs);
    } catch (error) {
      console.error("Error refreshing jobs:", error.message);
      if (jobsCache) setJobs(jobsCache);
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    jobsCache = null;
    lastJobsFetchTime = 0;
  };

  return { jobs, loading, refreshJobs, togglePinJob, deleteJob, clearCache };
};

/** Clear cache on logout so next user doesn't see stale data. */
export function clearJobsCacheOnLogout() {
  jobsCache = null;
  lastJobsFetchTime = 0;
}

export default useJob;
